// Self-hosted EN<->ZH translation (pprgb-translate), proxied at /translate/api/.

const CJK_RE = /[㐀-鿿豈-﫿]/g
const LETTER_RE = /\p{L}/gu
const URL_RE = /(?:https?|wss?):\/\/[^\s]+/g

export type TranslateDirection = {source: "en" | "zh"; target: "en" | "zh"}

/**
 * Guesses whether a message is Chinese or English by CJK character ratio
 * (measured over the prose only — urls stripped first, so a link-only
 * message doesn't count as "English"), and returns the translation
 * direction to offer — or `undefined` if the message is too short/link-only/
 * ambiguous for a translate button to be worth showing.
 */
export function detectTranslateDirection(content: string): TranslateDirection | undefined {
  const prose = content.trim().replace(URL_RE, " ").trim()
  if (prose.length > 2000) return undefined

  const letterCount = prose.match(LETTER_RE)?.length ?? 0
  if (letterCount < 3) return undefined

  const cjkCount = prose.match(CJK_RE)?.length ?? 0
  const ratio = cjkCount / letterCount

  if (ratio > 0.4) return {source: "zh", target: "en"}
  if (ratio < 0.05) return {source: "en", target: "zh"}
  return undefined
}

export async function translateText(text: string, direction: TranslateDirection): Promise<string> {
  const res = await fetch("/translate/api/translate", {
    method: "POST",
    headers: {"Content-Type": "application/json"},
    body: JSON.stringify({text, source: direction.source, target: direction.target}),
  })

  if (!res.ok) {
    throw new Error(`Translation failed (${res.status})`)
  }

  const data: {translated: string} = await res.json()
  return data.translated
}
